import { TrendingUp, TrendingDown, LucideIcon } from 'lucide-react';
import React from 'react';

interface StatsCardProps {
    title: string;
    value: string | number;
    icon?: LucideIcon | undefined;
    trend?: number | undefined;
    trendLabel?: string | undefined;
    variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | undefined;
    onClick?: (() => void) | undefined;
    isLoading?: boolean | undefined;
}

const StatsCard: React.FC<StatsCardProps> = ({
    title,
    value,
    icon: Icon,
    trend,
    trendLabel,
    variant = 'default',
    onClick,
    isLoading = false,
}) => {
    const variants = {
        default: 'bg-white border-slate-200',
        primary: 'bg-primary/5 border-primary/20',
        success: 'bg-emerald-50 border-emerald-200',
        warning: 'bg-amber-50 border-amber-200',
        danger: 'bg-red-50 border-red-200',
        info: 'bg-blue-50 border-blue-200',
    };

    const iconVariants = {
        default: 'bg-slate-100 text-slate-600',
        primary: 'bg-primary/10 text-primary',
        success: 'bg-emerald-100 text-emerald-600',
        warning: 'bg-amber-100 text-amber-600',
        danger: 'bg-red-100 text-red-600',
        info: 'bg-blue-100 text-blue-600',
    };

    if (isLoading) {
        return (
            <div className={`p-5 rounded-xl border ${variants[variant]} animate-pulse`}>
                <div className="flex items-start justify-between">
                    <div className="space-y-3 flex-1">
                        <div className="h-4 w-24 bg-slate-200 rounded" />
                        <div className="h-8 w-32 bg-slate-200 rounded" />
                    </div>
                    <div className="w-10 h-10 bg-slate-200 rounded-lg" />
                </div>
            </div>
        );
    }

    return (
        <div
            className={`
        p-5 rounded-xl border ${variants[variant]}
        ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}
      `}
            onClick={onClick}
        >
            <div className="flex items-start justify-between">
                <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-500">{title}</p>
                    <p className="text-2xl font-bold text-slate-900">{value}</p>

                    {/* Trend indicator */}
                    {trend !== undefined && (
                        <div className="flex items-center gap-1 pt-1">
                            {trend >= 0 ? (
                                <TrendingUp size={14} className="text-emerald-600" />
                            ) : (
                                <TrendingDown size={14} className="text-red-600" />
                            )}
                            <span
                                className={`text-xs font-medium ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'
                                    }`}
                            >
                                {trend >= 0 ? '+' : ''}
                                {trend}%
                            </span>
                            {trendLabel && (
                                <span className="text-xs text-slate-500">{trendLabel}</span>
                            )}
                        </div>
                    )}
                </div>

                {/* Icon */}
                {Icon && (
                    <div className={`p-2.5 rounded-lg ${iconVariants[variant]}`}>
                        <Icon size={20} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default StatsCard;
